"use client";

import type { FormVariant } from "@homeinstead/analytics";
import type { CareOptionType } from "~www/components/LeadForm/CareSeeker";
import type { CareSeekerLeadFormResponse } from "~www/types/forms/care-seeker-shallow-lead-form";
import type { LeadResponse } from "~www/types/forms/lead-response";

import { useCareSeekerSubmissionTracking } from "~www/hooks/useCareSeekerSubmissionTracking";
import { useLeadFormTrackingHandlers } from "~www/hooks/useLeadFormTrackingHandlers";

const ERROR_CONTEXT = "LeadForm";

const CARE_OPTION_KEYS = [
  "memoryCare",
  "everydayTasks",
  "mobilityAssistance",
  "other",
] as const;

function useLeadFormSubmissionTracking(
  state: LeadResponse<CareSeekerLeadFormResponse> | null,
  organizationNumber: number,
  persistedFieldValues: Record<string, string>,
  analyticsVersionId: string | undefined,
): void {
  const formVariant = (analyticsVersionId ?? "lfb") as FormVariant;

  const careFor = persistedFieldValues["careFor"] ?? "";
  const careOptions = CARE_OPTION_KEYS.filter(
    (key) => persistedFieldValues[`careType.${key}`] === "on",
  ) as CareOptionType[];

  const { trackError, trackSuccess } = useLeadFormTrackingHandlers({
    careFor,
    careOptions,
    contactInfo: {
      email: persistedFieldValues["email"] ?? "",
      firstName: persistedFieldValues["firstName"] ?? "",
      lastName: persistedFieldValues["lastName"] ?? "",
      phone: persistedFieldValues["phone"] ?? "",
    },
    formVariant,
    formVersionId: analyticsVersionId,
    organizationNumber,
  });

  useCareSeekerSubmissionTracking(state, {
    errorContext: ERROR_CONTEXT,
    organizationNumber,
    trackError,
    trackSuccess,
  });
}

export { useLeadFormSubmissionTracking };
